import React, { useState, useEffect, Fragment } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import swal from "sweetalert";
import { MapPin } from "lucide-react";
import Footer from "../Components/Footer";
import Header from "../Components/Header";
import methodModel from "../../methods/methods";

// Helper functions
const formatDate = (str) => {
  if (!str) return "—";
  const d = new Date(str);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatCurrency = (val) => {
  const num = Number(val ?? 0);
  const truncated = Math.trunc(num * 100) / 100;

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(truncated);
};

// Mock summary values
const MOCK_SUMMARY = {
  subtotal: 4300,
  tax: 400,
  discount: 200,
  total: 4500,
  paidAmount: 3000,
  dueAmount: 1500,
};

// Stepper Component for Shipment Progress
function StatusStepper({
  status,
  shipDate,
  trackingUrl,
  orderDate,
  updateDate,
}) {
  const currentStatus = (status || "").toLowerCase();

  // Status index mapping: 0 -> Received, 1 -> Preparing, 2 -> Shipped, 3 -> Delivered
  let activeStep = 0;
  if (currentStatus === "pending") {
    activeStep = 1;
  } else if (currentStatus === "shipped") {
    activeStep = 2;
  } else if (currentStatus === "delivered") {
    activeStep = 3;
  }

  return (
    <div className="border border-gray-200 rounded-2xl bg-white p-6 shadow-sm mb-6">
      <h3 className="text-sm font-semibold text-gray-900 border-b border-gray-100 pb-3.5 mb-6 flex items-center gap-2">
        <svg
          className="w-4 h-4 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
          />
        </svg>
        Tracking History
      </h3>
      <div className="relative pl-10 space-y-8">
        {/* Connector Line */}
        <div className="absolute left-[19px] top-4 bottom-4 w-0.5 bg-gray-200" />

        {/* Step 1: Order Received */}
        <div className="relative flex gap-4">
          <div className="absolute -left-[35px] z-10 flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 border-2 border-blue-600 text-white shadow-sm">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="3"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <div>
            <p className="text-sm font-bold text-gray-800">Order Received</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Order acknowledged and registered.
            </p>
            <p className="text-[10px] text-gray-450 mt-1 font-medium bg-gray-100 w-fit px-2 py-0.5 rounded-md">
              {formatDate(orderDate)}
            </p>
          </div>
        </div>

        {/* Step 2: Preparing Your Order */}
        <div className="relative flex gap-4">
          <div className="absolute -left-[35px] z-10 flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 border-2 border-blue-600 text-white shadow-sm">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
          </div>
          <div>
            <p className="text-sm font-bold text-gray-800">
              Preparing Your Order
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              Your package is being picked and packed.
            </p>
          </div>
        </div>

        {/* Step 3: Shipped */}
        <div className="relative flex flex-col gap-1.5">
          <div className="flex gap-4">
            <div
              className={`absolute -left-[35px] z-10 flex items-center justify-center w-8 h-8 rounded-full border-2 text-white shadow-sm transition-colors ${
                currentStatus === "shipped" || currentStatus === "delivered"
                  ? "bg-blue-600 border-blue-600"
                  : "bg-gray-500 border-gray-300 text-gray-400"
              }`}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1"
                />
              </svg>
            </div>
            <div>
              <p
                className={`text-sm font-bold ${currentStatus === "shipped" || currentStatus === "delivered" ? "text-gray-800" : "text-gray-400"}`}
              >
                Shipped
              </p>
              {currentStatus === "shipped" || currentStatus === "delivered" ? (
                <div className="text-xs text-gray-500 mt-1 space-y-1">
                  <p className="font-semibold text-gray-700">
                    Date: {formatDate(shipDate)}
                  </p>
                  {trackingUrl && (
                    <p className="truncate max-w-sm">
                      Tracking:{" "}
                      <a
                        href={trackingUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[#115B9D] hover:underline font-semibold"
                      >
                        {trackingUrl}
                      </a>
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-xs text-gray-400 mt-0.5">
                  Awaiting shipment details.
                </p>
              )}
            </div>
          </div>

          {/* Transit Sub-Checkpoints (only shown when shipped or delivered) */}
          {(currentStatus === "shipped" || currentStatus === "delivered") && (
            <div className="pl-4 mt-2 space-y-4 border-l border-blue-200 ml-4 py-1">
              <div className="relative pl-4">
                <div className="absolute left-[-21px] top-1.5 w-2.5 h-2.5 rounded-full bg-blue-500 border border-white" />
                <p className="text-xs font-semibold text-gray-700">
                  In Transit
                </p>
                <p className="text-[10px] text-gray-400">
                  Package left the fulfillment center.
                </p>
              </div>
              <div className="relative pl-4">
                <div className="absolute left-[-21px] top-1.5 w-2.5 h-2.5 rounded-full bg-blue-500 border border-white" />
                <p className="text-xs font-semibold text-gray-700">
                  Shipment Acknowledged
                </p>
                <p className="text-[10px] text-gray-400">
                  Carrier received shipping manifesto.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Step 4: Delivered */}
        <div className="relative flex gap-4">
          <div
            className={`absolute -left-[35px] z-10 flex items-center justify-center w-8 h-8 rounded-full border-2 text-white shadow-sm transition-colors ${
              currentStatus === "delivered"
                ? "bg-emerald-600 border-emerald-600"
                : "bg-gray-500 border-gray-300 text-gray-400"
            }`}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0"
              />
            </svg>
          </div>
          <div>
            <p
              className={`text-sm font-bold ${currentStatus === "delivered" ? "text-gray-800" : "text-gray-400"}`}
            >
              Delivered
            </p>
            {currentStatus === "delivered" ? (
              <div>
                <p className="text-xs text-gray-500 mt-0.5">
                  Package delivered successfully.
                </p>
                <p className="text-[10px] text-gray-450 mt-1 font-medium bg-emerald-50 text-emerald-700 w-fit px-2 py-0.5 rounded-md">
                  {formatDate(updateDate)}
                </p>
              </div>
            ) : (
              <p className="text-xs text-gray-400 mt-0.5">
                Awaiting final delivery.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function OrderTrackingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const triggerDate = searchParams.get("date");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  // Edit Shipping Date Modal states
  const [isEditShipDateOpen, setIsEditShipDateOpen] = useState(false);
  const [shipDateInput, setShipDateInput] = useState("");
  const [submittingShipDate, setSubmittingShipDate] = useState(false);

  // Edit Tracking URL Modal states
  const [isEditTrackingOpen, setIsEditTrackingOpen] = useState(false);
  const [trackingUrlInput, setTrackingUrlInput] = useState("");
  const [submittingTracking, setSubmittingTracking] = useState(false);

  // Fetch Order details
  const fetchOrderDetail = async () => {
    setLoading(true);
    loader(true);
    try {
      const res = await ApiClient.get("order-tracking/detail", { id });
      if (res?.success) {
        const orderData = res.data || res;
        setOrder(orderData);
        // Pre-fill shipping modal values
        setShipDateInput(orderData.shipDate || orderData.shippingDate || "");
        setTrackingUrlInput(orderData.trackingUrl || "");
      }
    } catch (err) {
      console.error("Error fetching order detail:", err);
    } finally {
      setLoading(false);
      loader(false);
    }
  };

  useEffect(() => {
    fetchOrderDetail();
  }, [id]);

  // Handle status update
  const handleStatusChange = (newStatus) => {
    swal({
      title: "Change Status?",
      text: `Are you sure you want to update status to "${newStatus}"?`,
      icon: "warning",
      buttons: ["Cancel", "Confirm"],
      dangerMode: true,
    }).then(async (confirmed) => {
      if (!confirmed) return;
      loader(true);
      try {
        const res = await ApiClient.put("order-tracking/status/change", {
          id: order._id || order.id,
          status: newStatus.toLowerCase(),
        });
        if (res?.success) {
          swal({
            title: "Updated",
            text: "Order status has been updated successfully.",
            icon: "success",
          });
          fetchOrderDetail();
        } else {
          swal({
            title: "Error",
            text: res?.message || "Failed to update order status.",
            icon: "error",
          });
        }
      } catch (err) {
        console.error("Error updating status:", err);
        swal({
          title: "Error",
          text: "An error occurred while updating status.",
          icon: "error",
        });
      } finally {
        loader(false);
      }
    });
  };

  // Handle shipping date update
  const handleShipDateSubmit = async (e) => {
    e.preventDefault();
    setSubmittingShipDate(true);
    loader(true);
    try {
      const res = await ApiClient.put("order-tracking/update", {
        id: order._id || order.id,
        shipDate: shipDateInput,
      });
      if (res?.success) {
        swal({
          title: "Saved",
          text: "Shipping date updated successfully.",
          icon: "success",
        });
        setIsEditShipDateOpen(false);
        fetchOrderDetail();
      } else {
        swal({
          title: "Error",
          text: res?.message || "Failed to update shipping date.",
          icon: "error",
        });
      }
    } catch (err) {
      console.error("Error updating shipping date:", err);
      swal({
        title: "Error",
        text: "An error occurred while updating shipping date.",
        icon: "error",
      });
    } finally {
      setSubmittingShipDate(false);
      loader(false);
    }
  };

  // Handle tracking URL update
  const handleTrackingSubmit = async (e) => {
    e.preventDefault();
    setSubmittingTracking(true);
    loader(true);
    try {
      const res = await ApiClient.put("order-tracking/update", {
        id: order._id || order.id,
        trackingUrl: trackingUrlInput,
      });
      if (res?.success) {
        swal({
          title: "Saved",
          text: "Tracking URL updated successfully.",
          icon: "success",
        });
        setIsEditTrackingOpen(false);
        fetchOrderDetail();
      } else {
        swal({
          title: "Error",
          text: res?.message || "Failed to update tracking URL.",
          icon: "error",
        });
      }
    } catch (err) {
      console.error("Error updating tracking URL:", err);
      swal({
        title: "Error",
        text: "An error occurred while updating tracking URL.",
        icon: "error",
      });
    } finally {
      setSubmittingTracking(false);
      loader(false);
    }
  };

  // Status badge style selectors
  const getStatusBadge = (status) => {
    const val = (status || "").toLowerCase();
    if (val === "pending") {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
          Pending
        </span>
      );
    }
    if (val === "shipped") {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
          Shipped
        </span>
      );
    }
    if (val === "delivered") {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          Delivered
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gray-50 text-gray-700 border border-gray-200">
        <span className="w-1.5 h-1.5 rounded-full bg-gray-500" />
        {status}
      </span>
    );
  };

  if (loading && !order) {
    return (
      <>
        <div className="flex items-center justify-center min-h-[400px]"></div>
      </>
    );
  }

  const orderStatus = (order.status || "").toLowerCase();
  const isShipped = orderStatus === "shipped";
  const isDelivered = orderStatus === "delivered";
  const isShippedOrDelivered = isShipped || isDelivered;

  return (
    <>
      <Header />
      <div className="min-h-[calc(100vh_-_75px)] md:min-h-[calc(100vh_-_95px)] mt-[80px] lg:mt-[100px]">
        <div className="container mx-auto px-5">
          <div className="py-8">
            <button
              onClick={() => navigate("/orders?tab=order_tracking")}
              className="flex items-center gap-1.5 text-sm text-[#115B9D] font-medium hover:underline mb-4"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.5"
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Back to list
            </button>
            {/* Details Grid */}
            <div className="grid grid-cols-12 gap-6 items-start">
              {/* Left / Secondary column (cards) */}
              <div className="space-y-5 col-span-12 lg:col-span-8 2xl:col-span-9">
                {/* Back and Header Section */}
                <div className="mb-6">
                  <div className="flex flex-wrap items-start justify-between gap-4 border border-gray-200 rounded-2xl bg-white p-6 shadow-sm">
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-semibold tracking-wider text-gray-400 uppercase">
                          Invoice
                        </span>
                        <span className="text-lg font-bold text-gray-900">
                          #{order?.invoiceNumber || "--"}
                        </span>
                        {getStatusBadge(order?.status)}
                      </div>
                      <div className="flex items-center gap-6 flex-wrap text-xs text-gray-500">
                        <p>
                          Invoice Status:{" "}
                          <span className="font-medium text-gray-700 capitalize">
                            {order.invoiceStatus == "partial_paid"
                              ? "Partial Paid"
                              : order.invoiceStatus || "--"}
                          </span>
                        </p>
                        <p>
                          Order Date:{" "}
                          <span className="font-medium text-gray-700">
                            {formatDate(order?.createdAt)}
                          </span>
                        </p>
                      </div>
                    </div>
                    <div>
                      {order?.status == "shipped" && (
                        <button
                          type="button"
                          onClick={(e) => handleStatusChange("delivered")}
                          className="inline-flex items-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-sm rounded-xl font-medium px-5 py-2.5 capitalize"
                        >
                          Mark As Delivered
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                {/* Customer Information Card */}
                <div className="border border-gray-200 rounded-2xl bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-semibold text-gray-900 border-b border-gray-100 pb-3 mb-4 flex items-center gap-2">
                    <svg
                      className="w-4 h-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    Customer Information
                  </h3>

                  {/* Customer Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                    <div>
                      <p className="text-[10px] uppercase font-semibold text-gray-400 tracking-wider">
                        Name
                      </p>
                      <p className="text-sm font-medium capitalize text-gray-800 mt-1">
                        {order.customerName || order.userId?.name || "—"}
                      </p>
                    </div>

                    <div>
                      <p className="text-[10px] uppercase font-semibold text-gray-400 tracking-wider">
                        Email
                      </p>

                      {order.userEmail || order.userId?.email ? (
                        <a
                          href={`mailto:${order.userEmail || order.userId?.email}`}
                          className="text-sm font-medium text-[#115B9D] hover:underline mt-1 block"
                        >
                          {order.userEmail || order.userId?.email}
                        </a>
                      ) : (
                        <p className="text-sm text-gray-500 mt-1">—</p>
                      )}
                    </div>
                  </div>

                  {/* Addresses */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Billing Address */}
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                          <MapPin size={15} className="text-blue-600" />
                        </div>

                        <div>
                          <h4 className="text-sm font-semibold text-gray-900">
                            Billing Address
                          </h4>
                          <p className="text-xs text-gray-500">
                            Invoice destination
                          </p>
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <p className="text-sm font-medium text-gray-900 leading-relaxed">
                          {order?.billingAddress?.Line1 || "N/A"}
                        </p>

                        <p className="text-sm text-gray-600">
                          {[
                            order?.billingAddress?.City,
                            order?.billingAddress?.PostalCode,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </p>

                        <p className="text-sm text-gray-600">
                          {order?.billingAddress?.Country}
                        </p>
                      </div>
                    </div>

                    {/* Shipping Address */}
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-lg bg-[#098f0926] flex items-center justify-center">
                          <MapPin size={15} className="text-[#098f09]" />
                        </div>

                        <div>
                          <h4 className="text-sm font-semibold text-gray-900">
                            Shipping Address
                          </h4>
                          <p className="text-xs text-gray-500">
                            Delivery destination
                          </p>
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <p className="text-sm font-medium text-gray-900 leading-relaxed">
                          {order?.shippingAddress?.Line1 || "N/A"}
                        </p>

                        <p className="text-sm text-gray-600">
                          {[
                            order?.shippingAddress?.City,
                            order?.shippingAddress?.PostalCode,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </p>

                        <p className="text-sm text-gray-600">
                          {order?.shippingAddress?.Country}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Products Table Card */}
                <div className="border border-gray-200 rounded-2xl bg-white p-5 lg:p-6 shadow-sm">
                  <h3 className="text-base font-semibold text-gray-900 border-b border-gray-100 pb-3.5 mb-4 flex items-center gap-2">
                    <svg
                      className="w-5 h-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                      />
                    </svg>
                    Ordered Items
                  </h3>
                  <div className="overflow-auto border border-gray-200 rounded-xl">
                    <table className="w-full min-w-[500px]">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          {["Item", "Unit", "Price", "Total"].map((h) => (
                            <th
                              key={h}
                              className="text-left py-3 px-4 font-semibold text-xs text-gray-900 uppercase tracking-wider"
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {order?.markupLineItems?.map((prod) => (
                          <tr key={prod.qboItemId}>
                            <td className="py-3.5 px-4 text-sm text-gray-500">
                              {prod.description}
                            </td>
                            <td className="py-3.5 px-4 text-sm text-gray-600 font-medium">
                              {prod.qty}
                            </td>
                            <td className="py-3.5 px-4 text-sm text-gray-600 font-medium">
                              {formatCurrency(prod.unitPrice)}
                            </td>
                            <td className="py-3.5 px-4 text-sm font-semibold text-gray-800">
                              {formatCurrency(prod.amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Right / Primary column (Stepper, Products & Summary) */}
              <div className="space-y-5 col-span-12 lg:col-span-4 2xl:col-span-3">
                {/* Shipping Info Card */}
                <div className="border border-gray-200 rounded-2xl bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-semibold text-gray-900 border-b border-gray-100 pb-3 mb-4 flex items-center gap-2">
                    <svg
                      className="w-4 h-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1"
                      />
                    </svg>
                    Shipping Details
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between border-b border-gray-50 pb-2">
                      <div>
                        <p className="text-[10px] uppercase font-semibold text-gray-400 tracking-wider">
                          Shipping Date
                        </p>
                        <p className="text-sm font-medium text-gray-800 mt-0.5">
                          {formatDate(order.shipDate || order.shippingDate)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[10px] uppercase font-semibold text-gray-400 tracking-wider">
                          Tracking Link
                        </p>
                        <div className="mt-1">
                          {order.trackingUrl ? (
                            <a
                              href={order.trackingUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-lg transition-colors border border-gray-200"
                            >
                              <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2.5"
                                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                />
                              </svg>
                              Track Shipment
                            </a>
                          ) : (
                            <span className="text-sm text-gray-400 italic">
                              No tracking link available
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Stepper progress */}
                <StatusStepper
                  status={order.status}
                  shipDate={order.shipDate || order.shippingDate}
                  trackingUrl={order.trackingUrl}
                  orderDate={order.createdAt}
                  updateDate={order.updatedAt}
                />

                {/* Order Summary Card */}
                <div className="border border-gray-200 rounded-2xl bg-white p-5 lg:p-6 shadow-sm w-full ml-auto">
                  <h3 className="text-base font-semibold text-gray-900 border-b border-gray-100 pb-3 mb-4">
                    Order Summary
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>Subtotal</span>
                      <span className="font-semibold text-gray-800">
                        {formatCurrency(
                          order?.invoiceDetails?.raw?.Line?.find(
                            (l) => l.DetailType === "SubTotalLineDetail",
                          )?.Amount || 0,
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>Tax</span>
                      <span className="font-semibold text-gray-800">
                        {formatCurrency(order?.invoiceDetails?.totalTax || 0)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>Discount</span>
                      <span className="font-semibold text-red-600">
                        -{" "}
                        {formatCurrency(
                          order?.invoiceDetails?.raw?.Line?.find(
                            (l) => l.DetailType === "DiscountLineDetail",
                          )?.Amount || 0,
                        )}
                      </span>
                    </div>
                    <div className="border-t border-gray-100 pt-3 flex justify-between text-base font-bold text-gray-900">
                      <span>Total Amount</span>
                      <span>
                        {formatCurrency(
                          order?.invoiceDetails?.totalAmount || 0,
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm pt-2 text-emerald-600">
                      <span>Paid Amount</span>
                      <span className="font-semibold">
                        {formatCurrency(order?.invoiceDetails?.paidAmount || 0)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm text-amber-600">
                      <span>Due Amount</span>
                      <span className="font-semibold">
                        {formatCurrency(
                          order?.invoiceDetails?.totalAmountDue || 0,
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Edit Shipping Date Modal */}
        <Transition appear show={isEditShipDateOpen} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-[999]"
            onClose={() => setIsEditShipDateOpen(false)}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-200"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-150"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 shadow-xl transition-all">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-bold text-gray-900 border-b border-gray-100 pb-3 mb-4"
                    >
                      Edit Shipped Date
                    </Dialog.Title>

                    <form onSubmit={handleShipDateSubmit} className="space-y-4">
                      <div>
                        <label className="block text-sm text-gray-700 font-medium mb-1.5">
                          Shipping Date
                        </label>
                        <input
                          type="date"
                          value={shipDateInput}
                          onChange={(e) => setShipDateInput(e.target.value)}
                          className="h-10 w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all bg-white"
                          required
                        />
                      </div>

                      <div className="flex justify-end gap-3 pt-3 border-t border-gray-100 mt-6">
                        <button
                          type="button"
                          onClick={() => setIsEditShipDateOpen(false)}
                          className="px-4 py-2 text-sm font-semibold border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
                          disabled={submittingShipDate}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="px-4 py-2 text-sm font-semibold bg-[#115B9D] hover:opacity-90 rounded-xl text-white transition-colors"
                          disabled={submittingShipDate}
                        >
                          {submittingShipDate
                            ? "Saving..."
                            : "Save Shipped Date"}
                        </button>
                      </div>
                    </form>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Edit Tracking Link Modal */}
        <Transition appear show={isEditTrackingOpen} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-[999]"
            onClose={() => setIsEditTrackingOpen(false)}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-200"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-150"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 shadow-xl transition-all">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-bold text-gray-900 border-b border-gray-100 pb-3 mb-4"
                    >
                      Edit Tracking Link
                    </Dialog.Title>

                    <form onSubmit={handleTrackingSubmit} className="space-y-4">
                      <div>
                        <label className="block text-sm text-gray-700 font-medium mb-1.5">
                          Tracking URL
                        </label>
                        <input
                          type="url"
                          placeholder="https://example.com/tracking"
                          value={trackingUrlInput}
                          onChange={(e) => setTrackingUrlInput(e.target.value)}
                          className="h-10 w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all bg-white"
                          required
                        />
                      </div>

                      <div className="flex justify-end gap-3 pt-3 border-t border-gray-100 mt-6">
                        <button
                          type="button"
                          onClick={() => setIsEditTrackingOpen(false)}
                          className="px-4 py-2 text-sm font-semibold border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
                          disabled={submittingTracking}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="px-4 py-2 text-sm font-semibold bg-[#115B9D] hover:opacity-90 rounded-xl text-white transition-colors"
                          disabled={submittingTracking}
                        >
                          {submittingTracking
                            ? "Saving..."
                            : "Save Tracking URL"}
                        </button>
                      </div>
                    </form>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </div>
      {methodModel.TokenChecker() && <Footer />}
    </>
  );
}
